import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UiPhoneCallerProps } from "../../models/components/phone-caller.model";
import { Formatter } from "../../services/formatter/formatter.service";
import { BsTelephoneFill } from "react-icons/bs";
import UiButton from "../button/Button";
import { classNames } from "primereact/utils";
import { Dialog } from "primereact/dialog";
import TwilioVoiceService from "../../services/twilio/twilio-voice.service";
import { getSessionStorageUser } from "../../services/session/auth.service";
import UiInputSwitch from "../input-switch/UiInputSwitch.component";
import { CallManagementEvent } from "../../models/call-management.model";

const UiPhoneCaller = (props: UiPhoneCallerProps) => {
	const { t } = useTranslation("common");
	const [showDialog, setShowDialog] = useState(false);
	const [isGroupCall, setIsGroupCall] = useState(false);
	const [hasActiveCall, setHasActiveCall] = useState(false);
	const [isCurrentCallConference, setIsCurrentCallConference] = useState(false);
	const [showConflictDialog, setShowConflictDialog] = useState(false);

	useEffect(() => {
		const sub = TwilioVoiceService.callManagementSubject.subscribe(
			(event?: CallManagementEvent) => {
				if (event) {
					setHasActiveCall(event.callManagementState.calls.length > 0);
					setIsCurrentCallConference(
						event.callManagementState.calls[0]?.state.isConference ?? false
					);
				}
			}
		);
		return () => sub.unsubscribe();
	}, []);

	const handleClose = () => {
		setShowDialog(false);
		setIsGroupCall(false);
	};

	const executeCall = () => {
		if (isGroupCall) {
			const coachPhone = `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`;
			TwilioVoiceService.startGroupCall(props.phoneNumber, coachPhone);
		} else {
			TwilioVoiceService.outboundCall(props.phoneNumber);
		}
		setIsGroupCall(false);
	};

	const handleCall = () => {
		setShowDialog(false);
		if (hasActiveCall && !isCurrentCallConference) {
			setShowConflictDialog(true);
			return;
		}
		executeCall();
	};

	const handleConflictConfirm = () => {
		setShowConflictDialog(false);
		const coachPhone = isGroupCall
			? `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`
			: undefined;
		TwilioVoiceService.hangUpAndCall(props.phoneNumber, isGroupCall, coachPhone);
		setIsGroupCall(false);
	};

	const dialogFooter = (
		<div className="k-call-confirm-footer">
			<UiButton
				label="UI_COMPONENTS.PHONE_CALLER.CANCEL"
				className="p-button-rounded p-button-outlined"
				onClick={handleClose}
			/>
			<UiButton
				label="UI_COMPONENTS.PHONE_CALLER.START_CALL"
				className="p-button-rounded"
				icon={<BsTelephoneFill />}
				onClick={handleCall}
			/>
		</div>
	);

	return (
		<>
			{props.phoneNumber && (
				<span className="k-phone-caller-container">
					{/* Phone number (formatted & non-clickable) */}
					<span className="phone-number">{Formatter.phoneNumber(props.phoneNumber)}</span>

					{/* Phone icon (clickable) */}
					<UiButton
						type="button"
						className={classNames(
							"p-button-rounded phone-icon",
							props.size || "p-button-sm"
						)}
						icon={<BsTelephoneFill />}
						onClick={() => setShowDialog(true)}
					/>

					{/* Call confirmation dialog */}
					<Dialog
						header={
							props.name
								? t("UI_COMPONENTS.PHONE_CALLER.DIALOG_TITLE", { name: props.name })
								: t("UI_COMPONENTS.PHONE_CALLER.DIALOG_TITLE_NO_NAME")
						}
						visible={showDialog}
						onHide={handleClose}
						draggable={false}
						resizable={false}
						className="k-call-confirm-dialog"
						footer={dialogFooter}
						focusOnShow={false}
					>
						<p className="k-call-confirm-message">
							{t("UI_COMPONENTS.PHONE_CALLER.DIALOG_MESSAGE", {
								phone: Formatter.phoneNumber(props.phoneNumber),
							})}
						</p>

						{/* Group call toggle — only shown when there is no active call */}
						{!hasActiveCall && (
							<div className="k-group-call-toggle">
								<div className="k-group-call-text">
									<label
										htmlFor="phone-caller-group-call"
										className="k-group-call-title"
									>
										{t("UI_COMPONENTS.DIALPAD.GROUP_CALL")}
									</label>
									<span className="k-group-call-subtitle">
										{t("UI_COMPONENTS.DIALPAD.GROUP_CALL_SUBTITLE")}
									</span>
								</div>
								<UiInputSwitch
									id="phone-caller-group-call"
									name="phoneCallerGroupCall"
									checked={isGroupCall}
									onChange={(e) => setIsGroupCall(e.value)}
									inputId="phone-caller-group-call"
								/>
							</div>
						)}
					</Dialog>

					{/* Active call conflict dialog */}
					<Dialog
						header={t("UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.TITLE")}
						visible={showConflictDialog}
						onHide={() => setShowConflictDialog(false)}
						draggable={false}
						resizable={false}
						className="k-call-confirm-dialog"
						footer={
							<div className="k-call-confirm-footer">
								<UiButton
									label="UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.CANCEL"
									className="p-button-rounded p-button-outlined"
									onClick={() => setShowConflictDialog(false)}
								/>
								<UiButton
									label="UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.CONFIRM"
									className="p-button-rounded p-button-danger"
									icon={<BsTelephoneFill />}
									onClick={handleConflictConfirm}
								/>
							</div>
						}
						focusOnShow={false}
					>
						<p className="k-call-confirm-message">
							{t("UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.MESSAGE")}
						</p>
					</Dialog>
				</span>
			)}
		</>
	);
};

export default UiPhoneCaller;
